import { isBanner, isCarousel, isModernBanner } from '@/components/card';
import { CardVariant } from '@/components/card/types';
import { ChipLabel } from '@/components/chip-label';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useProgramItemCountText } from '@rhg/hooks/use-program-item-count-text';
import commonMessages from '@rhg/lang/messages/common';
import { SxProps } from '@rhg/libs/material';
import { ProgramState } from '@rhg/utils/programs';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { CompletedStatus } from '../../completed-status';

interface TopLeftDecorationProps {
    programState: ProgramState;
    isTopRecommendation: boolean;
    variant: CardVariant;
    isClinicalProgram: boolean;
    displayCategoryDescriptionOnCarousel?: boolean;
    numSessions: number;
    meditationType?: string;
    sessionsText: string;
    progressText: (isChip: boolean) => string;
    sxImageIcon: SxProps;
    inProgressDTC: boolean;
    imageIconTop?: ReactNode;
}

export function TopLeftDecoration({
    programState,
    isTopRecommendation,
    variant,
    isClinicalProgram,
    displayCategoryDescriptionOnCarousel,
    numSessions,
    meditationType,
    sessionsText,
    progressText,
    sxImageIcon,
    inProgressDTC,
    imageIconTop,
}: TopLeftDecorationProps) {
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();
    const itemCountText = useProgramItemCountText(meditationType);
    if (imageIconTop) return imageIconTop;

    const isCarouselVariant = isCarousel(variant);

    // Top left decoration logic
    switch (programState) {
        case ProgramState.NotJoined:
        case ProgramState.Abandoned:
            if (!isBanner(variant) && isTopRecommendation) {
                return (
                    <ChipLabel
                        label={formatMessage(
                            commonMessages['common.top_recommendation']
                        )}
                        variant="light"
                        sx={sxImageIcon}
                    />
                );
            }
            if (isModernBanner(variant)) {
                return (
                    <ChipLabel
                        label={itemCountText(numSessions)}
                        variant="darkTransparent"
                        sx={sxImageIcon}
                    />
                );
            }
            if (
                (isMobile ||
                    (variant === CardVariant.CarouselSquare &&
                        displayCategoryDescriptionOnCarousel === false)) &&
                isClinicalProgram &&
                isCarouselVariant
            ) {
                return (
                    <ChipLabel
                        label={sessionsText}
                        variant="darkTransparent"
                        sx={sxImageIcon}
                    />
                );
            }
            break;

        case ProgramState.InProgress:
            if (!isCarouselVariant && !isModernBanner(variant)) {
                break;
            }
            if (displayCategoryDescriptionOnCarousel && isTopRecommendation) {
                return (
                    <ChipLabel
                        label={formatMessage(
                            commonMessages['common.top_recommendation']
                        )}
                        variant="light"
                        sx={sxImageIcon}
                    />
                );
            }
            // DTC progress is tracked but never displayed (EVE-13082) — an
            // in-progress DTC pack falls through to the inProgressDTC
            // handling below instead.
            if (!displayCategoryDescriptionOnCarousel && isClinicalProgram) {
                return (
                    <ChipLabel
                        label={progressText(true)}
                        variant="darkTransparent"
                        sx={sxImageIcon}
                    />
                );
            }
            break;

        case ProgramState.Completed:
            if (isBanner(variant)) {
                return <CompletedStatus sx={sxImageIcon} size="large" />;
            }
            if (
                isCarouselVariant &&
                displayCategoryDescriptionOnCarousel &&
                isTopRecommendation
            ) {
                return (
                    <ChipLabel
                        label={formatMessage(
                            commonMessages['common.top_recommendation']
                        )}
                        variant="light"
                        sx={sxImageIcon}
                    />
                );
            }
            if (isCarouselVariant && !displayCategoryDescriptionOnCarousel) {
                return (
                    <ChipLabel
                        label={sessionsText}
                        variant="darkTransparent"
                        sx={sxImageIcon}
                    />
                );
            }
            break;

        default:
            // Non-in-progress DTC programs
            if (
                (variant === 'horizontal' && isMobile) ||
                (isCarouselVariant && !displayCategoryDescriptionOnCarousel)
            ) {
                return (
                    <ChipLabel
                        label={sessionsText}
                        variant="darkTransparent"
                        sx={sxImageIcon}
                    />
                );
            }
            break;
    }

    // Handle inProgressDTC separately since it doesn't match the enum.
    // DTC progress is tracked but never displayed (EVE-13082) — in-progress
    // DTC content may still earn the top-recommendation chip, just never a
    // progress chip.
    if (
        inProgressDTC &&
        (isCarouselVariant || isModernBanner(variant)) &&
        displayCategoryDescriptionOnCarousel &&
        isTopRecommendation
    ) {
        return (
            <ChipLabel
                label={formatMessage(
                    commonMessages['common.top_recommendation']
                )}
                variant="light"
                sx={sxImageIcon}
            />
        );
    }

    return null;
}
